import type {
  Artifact,
  AtcInput,
  AtcStandard,
  ExecAtcSummary,
  RunLedgerRow,
} from '@capsio/contracts';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  api,
  artifactContentUrl,
  materialContentUrl,
  messageOf,
  uploadIngestFile,
} from '../api-client.js';

/**
 * ATC 执行页（D 段）。
 *
 * **这一页只看结果与证据。** 四区块：标准与边界 / 输入资料 / 执行台账 / 产出物。
 * 不做步骤流转、不做填格签字（ADR-0005、ADR-0006、B-1）。
 *
 * ## 文案曾写着这一页还能「做审批、触发任务」，而两样都没有（C-176 A2 / EX-02）
 *
 * `registerExecRoutes` 全部是 GET，没有审批端点，也没有派活的端点。
 * 页面却写着它能做——**界面不得承诺它给不出的东西**。
 * 人照着那句话去找入口，找不到时怀疑的是自己看漏了，不是那句话写错了。
 *
 * **裁定是改文案，不补动作**（Michael，甲案）：
 *
 * - V1 无实时能力、无自动分配代码路径（C-21），那个入口现在补出来也是空的
 * - 审批本来就是页签体系里独立的一项（审批中心），不该挤在执行页上
 * - ADR-0004 那三类动作讲的是 ATC 页**整体**允许什么，**不是每一页都要三样齐全**——
 *   把「允许」读成「必须」，就会为了凑齐而造一个假入口出来
 *
 * 这是今天第三次同一形态：点了没反应的按钮、状态列做成按钮形状却不可点、本条。
 */

const CARRIER_LABEL: Record<string, string> = {
  conversation: '对话记录',
  spreadsheet: '表格',
  document: '文档',
  drawing: '图纸',
  formal_document: '正式单据',
  image: '图片',
  archive: '归档包',
};

const TIME_SOURCE: Record<string, { label: string; strong: boolean }> = {
  exif: { label: '拍摄信息', strong: true },
  office_meta: { label: '文档属性', strong: true },
  filename: { label: '文件名', strong: false },
  embedded_doc: { label: '所在文档', strong: false },
  file_mtime: { label: '修改时间', strong: false },
};

/** 产出物种类的中文。管线四段各产一件。 */
const ARTIFACT_KIND: Record<string, string> = {
  raw: '原始采集',
  ledger: '入账台账',
  candidates: '候选清单',
  advice: '建议书',
};

/** 管线四段应产出的四件套。用于与实际产出对照，**缺的要看得见**。 */
const EXPECTED_KINDS = ['raw', 'ledger', 'candidates', 'advice'];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatTime(iso: string | null): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

function formatDuration(seconds: number | null): string {
  // null 而不是 0：未结束的执行没有时长，写 0 会被读成「瞬间完成」。
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds.toFixed(0)} 秒`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m} 分 ${s} 秒`;
}

/** 空值统一显示为「—」。**不编一个占位值**——没登记就是没登记。 */
function orDash(value: string | null | undefined): ReactNode {
  return value === null || value === undefined || value === '' ? (
    <span className="ex-none">—</span>
  ) : (
    <>{value}</>
  );
}

// ─────────────────────────────────────────────────────────────────
// 区块一  标准与边界
// ─────────────────────────────────────────────────────────────────

export function StandardBlock({ standard }: { standard: AtcStandard }): ReactNode {
  const test = standard.testSpec as { assert?: string } | null;

  return (
    <div className="ex-block">
      {/*
        Registration：这张卡**能**干什么，以及硬边界。
        对应原型 v0.22 的 Registration 分区（MAWA 三层的 CAN 那一层）。
      */}
      <section className="ex-card">
        <h3>
          Registration <span className="ex-sub">这张 ATC 能干什么 · 硬边界</span>
        </h3>
        <div className="ex-kv">
          <span className="ex-k">执行方式</span>
          <span>
            {standard.triggerType === 'manual' ? '人工触发' : standard.triggerType}
            {standard.hotlGate && <span className="chip is-warn">须人工审批（hotl_gate）</span>}
            <span className="ex-note">
              ATC 是任务卡，不是 Agent；每个 Agent 执行的工作必须是一张 ATC。
            </span>
          </span>
          <span className="ex-k">责任岗位</span>
          <span>
            {orDash(standard.positionName)}
            {standard.departmentName !== null && `（${standard.departmentName}）`}
            <span className="ex-note">唯一——ATC 只挂岗位</span>
          </span>
          <span className="ex-k">数据域</span>
          <span className="mono">{orDash(standard.dataDomain)}</span>
          <span className="ex-k">归属</span>
          <span>
            {standard.ownerScope === 'provider' ? '我方 ATC' : '客户 ATC'}
              状态 {standard.status}  成熟度 {standard.maturityLevel}
            {standard.determinism === 'deterministic' ? '确定性' : '探索性'}
          </span>
        </div>

        <div className="ex-seg">
          <div className="ex-seg-t">CAN · 允许做什么（能力登记，硬上限）</div>
          {standard.actionSteps.length === 0 ? (
            <p className="ex-none">
              卡上没有 ACTION 主序列（`action_spec.steps` 与 `how` 都读不出序列）。
            </p>
          ) : (
            <ol className="ex-steps">
              {standard.actionSteps.map((step, index) => (
                <li key={`${index}-${step}`}>{step}</li>
              ))}
            </ol>
          )}
          <p className="ex-note">仅限以上序列声明的动作；未登记项即越界。</p>
        </div>

        <div className="ex-seg is-hard">
          <div className="ex-seg-t">HARD BOUNDARY · 不得越界</div>
          <ul className="ex-bounds">
            <li>
              数据访问仅限本卡数据域{' '}
              <span className="mono">{standard.dataDomain ?? '（未配）'}</span>
                —— 分域即边界
            </li>
            <li>不得调用 ACTION 序列之外的工具与系统</li>
            {standard.hotlGate && <li>hotl_gate 未批不得置 success</li>}
            <li>不得反写蓝图；不得修改历史台账</li>
            <li>越界动作即 blocked 并落台账，不静默重试</li>
          </ul>
        </div>
      </section>

      {/* 四段定义。：ATC 只用这四段描述。 */}
      <section className="ex-card">
        <h3>
          任务定义 <span className="ex-sub">W · H · A · T 四段</span>
        </h3>
        <div className="ex-seg">
          <div className="ex-seg-t">WHAT · 目标与边界</div>
          <p>{standard.what}</p>
        </div>
        <div className="ex-seg">
          <div className="ex-seg-t">HOW · 方法与判据</div>
          <p>{standard.how}</p>
        </div>
        <div className="ex-seg">
          <div className="ex-seg-t">ACTION · 动作序列</div>
          <ol className="ex-steps">
            {standard.actionSteps.map((step, index) => (
              <li key={`a-${index}-${step}`}>{step}</li>
            ))}
          </ol>
        </div>
        {/* TEST 永不折叠：没有 TEST 的任务描述不是 ATC。 */}
        <div className="ex-seg is-test">
          <div className="ex-seg-t">TEST · 客观验证判据（永不折叠）</div>
          {test?.assert === undefined ? (
            <p className="ex-none">（空）—— 不满足 certified 前置条件</p>
          ) : (
            <p>{test.assert}</p>
          )}
        </div>
      </section>

      {/* 判据：只给地址不复述内容——复述一份就多了一份没人维护的判据。 */}
      <section className="ex-card">
        <h3>
          判据 <span className="ex-sub">约束本卡的裁定。只列地址，不复述内容</span>
        </h3>
        <table className="ex-table">
          <thead>
            <tr>
              <th>裁定</th>
              <th>在本卡管什么</th>
              <th>出处</th>
            </tr>
          </thead>
          <tbody>
            {standard.criteria.map((item) => (
              <tr key={item.label}>
                <td>{item.label}</td>
                <td>{item.bearing}</td>
                <td className="mono ex-path">{item.repoPath}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/*
          仓库里缺的判据**照实显示**。
          用五条看起来合理的话填上，这张卡从此显示着一份没人裁定过的判据，
          而读它的人无从分辨哪几条是真裁定。
        */}
        {standard.missingCriteria.map((item) => (
          <div key={item.label} className="ex-missing">
            <strong>缺：{item.label}</strong>
            <p>{item.note}</p>
            <p className="ex-note">期望位置：{item.expectedAt}</p>
          </div>
        ))}
      </section>

      {/* 输出要求：卡上声明的 vs 实际产出过的，对照着看。 */}
      <section className="ex-card">
        <h3>
          输出要求 <span className="ex-sub">卡上声明的产出物</span>
        </h3>
        {standard.declaredOutputs.length === 0 ? (
          <p className="ex-none">
            卡上没有声明产出物（`action_spec.outputs` 为空）。
            下面是这张卡**实际产出过**的种类，它不能替代声明——
            实际产出是事实，声明是要求，两者对不上的时候要看得出来。
          </p>
        ) : (
          <ul className="ex-steps">
            {standard.declaredOutputs.map((out) => (
              <li key={out}>{out}</li>
            ))}
          </ul>
        )}
        <p className="ex-observed">
          实际产出过：
          {standard.observedOutputKinds.length === 0
            ? ' —'
            : standard.observedOutputKinds.map((kind) => ARTIFACT_KIND[kind] ?? kind).join('、')}
        </p>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 区块二  输入资料
// ─────────────────────────────────────────────────────────────────

function InputRow({ row }: { row: AtcInput }): ReactNode {
  const source = row.contentTimeSource === null ? undefined : TIME_SOURCE[row.contentTimeSource];
  const isImage = row.mediaType.startsWith('image/');

  return (
    <li className="ex-input">
      {/*
        图纸与图片给缩略信息。浏览器渲染不了 tif / pdf 的缩略图，
        那时给尺寸与类型——**不给一个假的占位图**，占位图会让人以为看过了。
      */}
      <div className="ex-thumb">
        {isImage ? (
          <img src={materialContentUrl(row.materialId)} alt={row.originalName ?? ''} />
        ) : (
          <span className="ex-thumb-none">{row.mediaType.split('/')[1] ?? '文件'}</span>
        )}
      </div>
      <div className="ex-input-main">
        <a href={materialContentUrl(row.materialId)} target="_blank" rel="noreferrer">
          {row.originalName ?? '（无名）'}
        </a>
        <div className="ex-input-meta">
          <span className="sub-hint">{formatBytes(row.byteSize)}</span>
          {row.derivedCount > 0 && <span className="chip">含 {row.derivedCount} 张内嵌图</span>}
          {row.origin === 'consumed' && <span className="chip">本次执行已用</span>}
          {row.origin === 'uploaded_here' && <span className="chip">在本卡上传</span>}
        </div>
        {row.sourcePath !== null && row.sourcePath !== row.originalName && (
          <p className="ex-path mono">{row.sourcePath}</p>
        )}
      </div>
      {/* 两个时间维度并列，出处标强弱。 */}
      <div className="ex-times">
        <span className="ex-time">
          <span className="ex-time-tag">导入</span> {formatTime(row.uploadedAt)}
        </span>
        <span className="ex-time">
          <span className="ex-time-tag">资料</span> {formatTime(row.contentTime)}
          {source !== undefined && (
            <span className={source.strong ? 'ex-src is-strong' : 'ex-src'}>{source.label}</span>
          )}
        </span>
      </div>
    </li>
  );
}

function InputsBlock({ code, onChanged }: { code: string; onChanged: () => void }): ReactNode {
  const [rows, setRows] = useState<AtcInput[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const picker = useRef<HTMLInputElement | null>(null);

  const reload = useCallback((): void => {
    void api.execInputs(code).then(
      (data) => {
        setRows(data.rows);
        setError(null);
      },
      (cause: unknown) => setError(messageOf(cause)),
    );
  }, [code]);

  useEffect(reload, [reload]);

  /**
   * 上传。**走记录池那条路**（`/api/ingest/*`），只是开批次时带上本卡编码——
   * 入库时顺手登记一条输入资料。这里没有、也不该有第二个上传端点（ADR-0039 二）。
   */
  const send = useCallback(
    async (files: File[]): Promise<void> => {
      if (files.length === 0 || busy) return;
      setBusy(true);
      setNote(null);
      try {
        const { batchId } = await api.openIngestBatch({
          label: `${code} 输入资料`,
          provenance: 'client_provided',
          atcCode: code,
        });
        let stored = 0;
        let deduped = 0;
        let media = 0;
        for (const file of files) {
          const result = await uploadIngestFile(batchId, file, 'client_provided');
          if (result.outcome === 'stored') stored += 1;
          if (result.outcome === 'deduplicated') deduped += 1;
          media += result.mediaNew;
        }
        setNote(
          `已交进 ${files.length} 份：新入库 ${stored}、内容已在库 ${deduped}` +
            (media > 0 ? `，另提取内嵌图片 ${media} 张` : ''),
        );
        reload();
        onChanged();
      } catch (cause) {
        setError(messageOf(cause));
      } finally {
        setBusy(false);
      }
    },
    [busy, code, reload, onChanged],
  );

  if (error !== null) return <p className="msg">{error}</p>;
  if (rows === null) return <p className="sub-hint">读取中……</p>;

  return (
    <div className="ex-block">
      <section className="ex-card">
        <h3>
          交资料 <span className="ex-sub">交进来即去重、即提取内嵌图片——与记录池同一条路</span>
        </h3>
        <div
          className={`ex-drop${dragging ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            void send([...event.dataTransfer.files]);
          }}
        >
          <p>把资料拖到这里，或</p>
          <button
            className="btn"
            type="button"
            disabled={busy}
            onClick={() => picker.current?.click()}
          >
            {busy ? '交进中…' : '选文件'}
          </button>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              void send([...(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />
          {/*
            这一句是边界本身：交材料不是「在界面上干活」，
            所以它不构成被禁的那类界面（ADR-0032 三、B-1）。
          */}
          <p className="ex-note">
            资料进的是跨 ATC 的记录池，这里登记的是「本卡用到它」——
            **登记不是归属**，同一份资料可以被多张卡用。
          </p>
        </div>
        {note !== null && <p className="ex-ok">{note}</p>}
      </section>

      <ArchivedList rows={rows} />
    </div>
  );
}

/** 已归档清单，按载体分类。纯渲染——真实数据在测试里直接喂进来。 */
export function ArchivedList({ rows }: { rows: AtcInput[] }): ReactNode {
  const groups = new Map<string, AtcInput[]>();
  for (const row of rows) {
    const list = groups.get(row.carrierKind) ?? [];
    list.push(row);
    groups.set(row.carrierKind, list);
  }

  return (
    <section className="ex-card">
      <h3>
        已归档清单 <span className="ex-sub">共 {rows.length} 份 · 按载体分类</span>
      </h3>
      {rows.length === 0 ? (
        <p className="ex-none">还没有登记到本卡的资料。</p>
      ) : (
        [...groups.entries()].map(([kind, list]) => (
          <div key={kind} className="ex-group">
            <p className="ex-group-h">
              {CARRIER_LABEL[kind] ?? kind}
              <span className="sub-hint"> {list.length} 份</span>
            </p>
            <ul className="ex-inputs">
              {list.map((row) => (
                <InputRow key={`${row.materialId}-${row.runRef ?? 'none'}`} row={row} />
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// 区块三  执行台账
// ─────────────────────────────────────────────────────────────────

export function LedgerBlock({
  rows,
  selected,
  onSelect,
}: {
  rows: RunLedgerRow[];
  selected: string | null;
  onSelect: (runRef: string) => void;
}): ReactNode {
  return (
    <div className="ex-block">
      <section className="ex-card">
        <h3>
          执行台账{' '}
          <span className="ex-sub">
            共 {rows.length} 次 · 仅追加、无修改—— 每一次执行有账可查
          </span>
        </h3>
        {rows.length === 0 ? (
          <p className="ex-none">还没有执行记录。</p>
        ) : (
          <div className="table-wrap">
            <table className="ex-table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>执行方</th>
                  <th>触发情境</th>
                  <th>责任人</th>
                  <th>时长</th>
                  <th>关键输出</th>
                  <th>结果</th>
                  <th>自评</th>
                  <th>证据</th>
                  <th>输出包</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const evidence = row.testEvidence as Record<string, unknown> | null;
                  const evidenceKeys =
                    evidence === null ? [] : Object.keys(evidence).filter((k) => k !== '');
                  return (
                    <tr
                      key={row.runRef}
                      className={selected === row.runRef ? 'is-open' : undefined}
                      onClick={() => onSelect(row.runRef)}
                    >
                      <td>{formatTime(row.startedAt)}</td>
                      <td>
                        {row.actorType === 'hub'
                          ? '中枢'
                          : row.actorType === 'human'
                            ? '人'
                            : '人机'}
                        <span className="ex-note mono">{row.actorId}</span>
                      </td>
                      <td>{orDash(row.triggerRef)}</td>
                      <td>{orDash(row.responsible)}</td>
                      <td>{formatDuration(row.durationSeconds)}</td>
                      <td>{orDash(ARTIFACT_KIND[row.keyOutput ?? ''] ?? row.keyOutput)}</td>
                      <td>
                        <span className={row.status === 'success' ? 'ex-ok-tag' : 'ex-bad-tag'}>
                          {row.status}
                        </span>
                      </td>
                      {/* 自评：not_evaluated 是「还没评」，不是「评过了没问题」。 */}
                      <td>
                        {row.testResult === 'not_evaluated' ? (
                          <span className="ex-none">未自评</span>
                        ) : (
                          row.testResult
                        )}
                      </td>
                      <td>
                        {evidenceKeys.length === 0 ? (
                          <span className="ex-none">—</span>
                        ) : (
                          <span className="mono ex-ev">{evidenceKeys.length} 项</span>
                        )}
                      </td>
                      <td>
                        {row.artifactCount === 0 ? (
                          <span className="ex-none">—</span>
                        ) : (
                          <button className="linkbtn" type="button">
                            {row.artifactCount} 件 · v{row.artifactVersion}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="ex-note">
          点一行看它的产出物。空白列是**真的没有登记**，不是没显示——
          触发情境、责任人、自评三列在本批数据里为空，见页脚说明。
        </p>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 区块四  产出物
// ─────────────────────────────────────────────────────────────────

function ArtifactsBlock({ runRef }: { runRef: string | null }): ReactNode {
  // 没选执行时**不挂载加载组件**——挂了就得在 effect 里把上一次的结果清掉，
  // 而那会多渲染一帧旧数据。换执行时靠 key 重挂，状态随之重来。
  if (runRef === null) return <p className="sub-hint">在执行台账里点一次执行，看它的产出物。</p>;
  return <ArtifactsLoader key={runRef} runRef={runRef} />;
}

function ArtifactsLoader({ runRef }: { runRef: string }): ReactNode {
  const [rows, setRows] = useState<Artifact[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.execArtifacts(runRef).then(
      (data) => {
        setRows(data.rows);
        setError(null);
      },
      (cause: unknown) => setError(messageOf(cause)),
    );
  }, [runRef]);

  if (error !== null) return <p className="msg">{error}</p>;
  if (rows === null) return <p className="sub-hint">读取中……</p>;

  return <ArtifactsView rows={rows} runRef={runRef} />;
}

/** 产出物清单。纯渲染——四件套缺了几件由它算并显示。 */
export function ArtifactsView({ rows, runRef }: { rows: Artifact[]; runRef: string }): ReactNode {
  const present = new Set(rows.map((row) => row.kind));
  const missing = EXPECTED_KINDS.filter((kind) => !present.has(kind));

  return (
    <div className="ex-block">
      <section className="ex-card">
        <h3>
          产出物{' '}
          <span className="ex-sub">
            稳定标识 = 卡编码 + 执行标识 + 版本，三者永不变更
          </span>
        </h3>
        <p className="ex-note mono">run_ref {runRef}</p>

        <div className="table-wrap">
          <table className="ex-table">
            <thead>
              <tr>
                <th>版本</th>
                <th>种类</th>
                <th>大小</th>
                <th>产出时间</th>
                <th>产出方</th>
                <th>内容哈希</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.artifactId}>
                  <td>v{row.version}</td>
                  <td>{ARTIFACT_KIND[row.kind] ?? row.kind}</td>
                  <td>{formatBytes(row.byteSize)}</td>
                  <td>{formatTime(row.producedAt)}</td>
                  <td className="mono">{row.producedBy}</td>
                  <td className="mono ex-hash">{row.contentHash.slice(0, 12)}…</td>
                  <td>
                    <a
                      className="linkbtn"
                      href={artifactContentUrl(row.artifactId)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      下载
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/*
          四件套里缺的那些**要看得见**。
          只列出已有的三件，读的人会以为这次执行本来就该产出三件。
        */}
        {missing.length > 0 && (
          <div className="ex-missing">
            <strong>四件套缺 {missing.length} 件</strong>
            <p>
              管线四段各产一件，本次执行只落了 {rows.length} 件；缺
              {missing.map((kind) => ARTIFACT_KIND[kind] ?? kind).join('、')}。
            </p>
            <p className="ex-note">这是数据的实情，不是本页少显示了——缺的那一段没有产出物落库。</p>
          </div>
        )}
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────

type BlockKey = 'standard' | 'inputs' | 'runs' | 'artifacts';

const BLOCKS: { key: BlockKey; label: string }[] = [
  { key: 'standard', label: '标准与边界' },
  { key: 'inputs', label: '输入资料' },
  { key: 'runs', label: '执行台账' },
  { key: 'artifacts', label: '产出物' },
];

export function ExecPage({
  code,
  onPick,
}: {
  code: string | null;
  onPick: (code: string) => void;
}): ReactNode {
  // 换卡即重挂：状态随组件重来，不必在 effect 里逐个清空。
  return <ExecCard key={code ?? '(none)'} code={code} onPick={onPick} />;
}

function ExecCard({
  code,
  onPick,
}: {
  code: string | null;
  onPick: (code: string) => void;
}): ReactNode {
  const [atcs, setAtcs] = useState<ExecAtcSummary[] | null>(null);
  const [standard, setStandard] = useState<AtcStandard | null>(null);
  const [runs, setRuns] = useState<RunLedgerRow[]>([]);
  const [block, setBlock] = useState<BlockKey>('standard');
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    void api.execAtcs().then(
      (data) => setAtcs(data.rows),
      (cause: unknown) => setError(messageOf(cause)),
    );
  }, []);

  /*
   * 换卡时不在 effect 里把状态一个个清空——那会多渲染一帧旧卡的内容，
   * 且 React 的 lint 正确地拦下了它。改由外层用 `key={code}` 重挂本组件，
   * 状态随组件一起重来，不需要清。
   */
  useEffect(() => {
    if (code === null) return;
    void api.execStandard(code).then(setStandard, (cause: unknown) => setError(messageOf(cause)));
    void api.execRuns(code).then(
      (data) => {
        setRuns(data.rows);
        // 默认选最近一次执行，产出物那一块进去就有东西看。
        setSelectedRun(data.rows[0]?.runRef ?? null);
      },
      (cause: unknown) => setError(messageOf(cause)),
    );
  }, [code, reloadKey]);

  if (error !== null) return <p className="msg">{error}</p>;
  if (atcs === null) return <p className="sub-hint">读取中……</p>;

  if (code === null) {
    return (
      <div className="content-in">
        <div className="page-head">
          <div>
            <h1>ATC 执行页</h1>
            <p className="sub-hint">
              挑一张卡。这一页只看结果与证据——不做步骤流转，不做填格签字。
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="ex-table">
            <thead>
              <tr>
                <th>编码</th>
                <th>名称</th>
                <th>岗位</th>
                <th>归属</th>
                <th>状态</th>
                <th>执行次数</th>
                <th>最近一次</th>
              </tr>
            </thead>
            <tbody>
              {atcs.map((row) => (
                <tr key={row.code} className="ex-pick" onClick={() => onPick(row.code)}>
                  <td className="mono">{row.code}</td>
                  <td>{row.name}</td>
                  <td>{orDash(row.positionName)}</td>
                  <td>{row.ownerScope === 'provider' ? '我方' : '客户'}</td>
                  <td>{row.status}</td>
                  <td>{row.runCount}</td>
                  <td>{formatTime(row.lastRunAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="content-in">
      <div className="page-head">
        <div>
          <h1>{standard?.name ?? code}</h1>
          <p className="sub-hint mono">{code}</p>
        </div>
        <button className="linkbtn" type="button" onClick={() => onPick('')}>
          ← 换一张卡
        </button>
      </div>

      <div className="segmented ex-tabs" role="tablist" aria-label="执行页区块">
        {BLOCKS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={block === item.key}
            onClick={() => setBlock(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {block === 'standard' &&
        (standard === null ? (
          <p className="sub-hint">读取中……</p>
        ) : (
          <StandardBlock standard={standard} />
        ))}
      {block === 'inputs' && (
        <InputsBlock code={code} onChanged={() => setReloadKey((key) => key + 1)} />
      )}
      {block === 'runs' && (
        <LedgerBlock
          rows={runs}
          selected={selectedRun}
          onSelect={(runRef) => {
            setSelectedRun(runRef);
            setBlock('artifacts');
          }}
        />
      )}
      {block === 'artifacts' && <ArtifactsBlock runRef={selectedRun} />}
    </div>
  );
}
